import { useIntl } from 'react-intl';

import { Icon, TIconNames } from 'components/Icon';
import {
  DashboardIcon,
  DocumentIcon,
  GraduationHatIcon,
  HomeIcon,
  SettingsSidebarIcon,
  SupportStaffIcon,
  ViolationsIcon,
  ReportingIcon,
} from 'componentsShared/Icons';
import { allowedUserTypes } from 'routes/RoutesData';
import { usePmPermissions } from 'hooks/usePmPermissions';
import { ECapabilityKey } from 'data/types/permissions.types';

const defaultTabs = [
  {
    label: 'dashboard',
    route: 'dashboard',
    icon: <DashboardIcon />,
    dataCy: 'dashboard',
  },
  {
    label: 'animals',
    route: 'animals',
    icon: <Icon icon={TIconNames.FOOTPRINT} />,
    subRoutes: [
      { label: 'allAnimals', route: 'animals/all' },
      { label: 'visitationAnimals', route: 'animals/visitation' },
    ],
  },
  {
    label: 'residents',
    route: 'residents',
    icon: <Icon icon={TIconNames.PEOPLE} />,
  },
  {
    label: 'properties',
    route: 'properties',
    icon: <HomeIcon />,
    dataCy: 'properties',
  },
  {
    label: 'team-members',
    route: 'property-managers',
    icon: <SupportStaffIcon />,
    dataCy: 'managers',
  },
  {
    label: 'policies',
    route: 'policies',
    icon: <DocumentIcon />,
    dataCy: 'policies',
  },
];

export const useTabList = ({ impersonate, profile }) => {
  const { formatMessage } = useIntl();
  const { landlord, propertyManager } = allowedUserTypes;

  const { can, hasAccessToCourses } = usePmPermissions();
  const hasAccessToBillingMethod = can(ECapabilityKey.ACCESS_TO_BILLING_METHOD);
  const hasAccessToReports = can(ECapabilityKey.AUTOMATED_REPORTING_ACCESS);
  const hasTeamMemberAccess = can(ECapabilityKey.TEAM_MEMBERS_ACCESS);

  return {
    [landlord]: [
      ...defaultTabs.map((tab) => ({
        ...tab,
        label: formatMessage({ id: `tabs.${tab.label}` }),
        subRoutes: (tab.subRoutes || []).map((sub) => ({
          ...sub,
          label: formatMessage({ id: `tabs.${sub.label}` }),
        })),
      })),
      ...(profile?.hasAccessToCourses
        ? [
            {
              label: formatMessage({ id: `tabs.curriculum` }),
              route: 'curriculum',
              icon: <GraduationHatIcon color='#FCC425' />,
            },
          ]
        : []),
      {
        label: formatMessage({ id: `tabs.complaints` }),
        route: 'complaints',
        icon: <ViolationsIcon />,
        dataCy: 'complaints',
      },
      ...(hasAccessToReports || impersonate
        ? [
            {
              label: formatMessage({ id: 'tabs.reporting' }),
              route: 'reporting',
              icon: <ReportingIcon />,
              dataCy: 'reporting',
            },
          ]
        : []),
      {
        label: formatMessage({ id: `tabs.settings` }),
        route: 'settings',
        icon: <SettingsSidebarIcon />,
        subRoutes: [
          { label: formatMessage({ id: `tabs.payments` }), route: 'settings' },
          { label: formatMessage({ id: `tabs.integrations` }), route: 'integration-settings' },
          { label: formatMessage({ id: `tabs.notifications` }), route: `notifications/${profile?.id}` },
          { label: formatMessage({ id: `tabs.account_settings` }), route: 'account-settings' },
          ...(impersonate
            ? [
                { label: formatMessage({ id: `tabs.Sso` }), route: 'authentication-settings' },
                { label: formatMessage({ id: `tabs.Scim` }), route: 'scim-settings' },
              ]
            : []),
        ],
      },
      {
        label: formatMessage({ id: `tabs.support` }),
        route: 'support',
        icon: <Icon icon={TIconNames.QUESTION_MARK} />,
        dataCy: 'support',
      },
    ],
    [propertyManager]: [
      ...defaultTabs
        .filter((tab) => !(tab.label === 'team-members' && !hasTeamMemberAccess))
        .map((tab) => ({
          ...tab,
          label: formatMessage({ id: `tabs.${tab.label}` }),
          subRoutes: (tab.subRoutes || []).map((sub) => ({
            ...sub,
            label: formatMessage({ id: `tabs.${sub.label}` }),
          })),
        })),
      ...(hasAccessToCourses
        ? [
            {
              label: formatMessage({ id: `tabs.curriculum` }),
              route: 'curriculum',
              icon: <GraduationHatIcon color='#FCC425' />,
            },
          ]
        : []),
      {
        label: formatMessage({ id: `tabs.complaints` }),
        route: 'complaints',
        icon: <ViolationsIcon />,
        dataCy: 'complaints',
      },
      ...(hasAccessToReports || impersonate
        ? [
            {
              label: formatMessage({ id: `tabs.reporting` }),
              route: 'reporting',
              icon: <ReportingIcon />,
              dataCy: 'reporting',
            },
          ]
        : []),
      ...(impersonate
        ? []
        : [
            {
              label: formatMessage({ id: `tabs.support` }),
              route: 'support',
              icon: <Icon icon={TIconNames.QUESTION_MARK} />,
              dataCy: 'support',
            },
          ]),
      {
        label: formatMessage({ id: `tabs.settings` }),
        route: hasAccessToBillingMethod ? 'settings' : `notifications/${profile?.id}`,
        icon: <SettingsSidebarIcon />,
        subRoutes: [
          ...(hasAccessToBillingMethod ? [{ label: formatMessage({ id: 'tabs.payments' }), route: 'settings' }] : []),
          { label: formatMessage({ id: 'tabs.notifications' }), route: `notifications/${profile?.id}` },
          { label: formatMessage({ id: 'tabs.account_settings' }), route: 'account-settings' },
        ],
      },
    ],
  };
};
